import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type {
  ActivityEntryDoc,
  CardDoc,
  KycVerificationDoc,
  PaymentDoc,
  UserDoc,
  WithdrawalDoc,
} from "@/firestore/types";
import type { LiveQueryState, QuerySpec } from "@/firestore/hooks";

dayjs.extend(utc);
dayjs.extend(timezone);

export const ROW_LIMIT = 5_000;
export const HOUR = 3_600_000;
export type Period = "today" | "24h" | "7d";
export interface Window {
  start: number;
  end: number;
  bucketMs: number;
}

export function dashboardWindow(period: Period, now: number, zone: string): Window {
  return {
    start:
      period === "today"
        ? dayjs(now).tz(zone).startOf("day").valueOf()
        : now - (period === "7d" ? 168 : 24) * HOUR,
    end: now,
    bucketMs: HOUR,
  };
}

/** Date bounds must be native Dates: these Firestore fields contain Timestamps, not strings. */
export function windowQuery(field: string, start: number): QuerySpec {
  return {
    filters: [[field, ">=", new Date(start)]],
    orderBy: [[field, "desc"]],
    limit: ROW_LIMIT + 1,
  };
}

export function sourceProblem(source: LiveQueryState<unknown[]>, allAges = false): string | null {
  if (source.error) return "Unavailable — check access or retry the connection.";
  if (source.loading) return "Loading…";
  if (source.fromCache) return "Waiting for server data. Cached results are not used for totals.";
  if (source.data.length > ROW_LIMIT)
    return `More than ${ROW_LIMIT.toLocaleString()} records. ${allAges ? "Explore the withdrawals list" : "Choose a shorter window"}; totals are hidden.`;
  return null;
}

export function inWindow(date: string | null | undefined, window: Window): boolean {
  const ms = date ? Date.parse(date) : NaN;
  return ms >= window.start && ms <= window.end;
}

export function sumMoney(values: readonly number[]): bigint | null {
  if (values.some((v) => !Number.isSafeInteger(v))) return null;
  return values.reduce((total, value) => total + BigInt(value), 0n);
}

/** Preserve every microUSDC, including for totals larger than Number.MAX_SAFE_INTEGER. */
export function money(value: bigint | null): string {
  if (value === null) return "Unavailable";
  const abs = value < 0n ? -value : value;
  const fraction = (abs % 1_000_000n).toString().padStart(6, "0").replace(/0+$/, "");
  return `${value < 0n ? "−" : ""}${(abs / 1_000_000n).toLocaleString("en-US")}${fraction ? `.${fraction}` : ""}`;
}

export type Signup = Pick<UserDoc, "id" | "createdAt">;
export type Verification = Pick<
  KycVerificationDoc,
  | "id"
  | "user"
  | "state"
  | "createdAt"
  | "updatedAt"
  | "startedAt"
  | "submittedAt"
  | "rejectionReason"
>;
export type Card = Pick<CardDoc, "id" | "owner" | "state" | "createdAt" | "tokenWallets">;
export type Entry = Pick<
  ActivityEntryDoc,
  "id" | "owner" | "category" | "occurredAt" | "amountMicroUsdc"
>;
export type Withdrawal = Pick<
  WithdrawalDoc,
  "id" | "owner" | "state" | "createdAt" | "updatedAt" | "sourceAmountMicroUsdc"
>;
export type Payment = Pick<PaymentDoc, "id" | "state" | "createdAt" | "amountMicroUsdc">;

function unique<T extends { id: string }>(rows: readonly T[]): T[] {
  return [...new Map(rows.map((row) => [row.id, row])).values()];
}

export function onboarding(
  users: readonly Signup[],
  attempts: readonly Verification[],
  cards: readonly Card[],
  window: Window,
) {
  const cohort = new Set(users.filter((u) => inWindow(u.createdAt, window)).map((u) => u.id));
  const latest = new Map<string, Verification>();
  for (const attempt of attempts) {
    if (!cohort.has(attempt.user) || !inWindow(attempt.createdAt, window)) continue;
    const old = latest.get(attempt.user);
    // A late update to a predecessor must never replace the newest retry session.
    if (
      !old ||
      Date.parse(attempt.createdAt) > Date.parse(old.createdAt) ||
      (attempt.createdAt === old.createdAt && attempt.id > old.id)
    )
      latest.set(attempt.user, attempt);
  }
  const states: Record<string, number> = { noVerification: cohort.size - latest.size };
  let regionRejected = 0;
  for (const attempt of latest.values()) {
    states[attempt.state] = (states[attempt.state] ?? 0) + 1;
    if (attempt.state === "rejected" && attempt.rejectionReason?.includes("WRONG_USER_REGION"))
      regionRejected++;
  }
  const issuedStates = new Set(["active", "frozen", "locked", "terminated", "replaced"]);
  const issued = cards.filter(
    (c) => cohort.has(c.owner) && inWindow(c.createdAt, window) && issuedStates.has(c.state),
  );
  return {
    users: cohort.size,
    started: [...latest.values()].filter((v) => v.startedAt || v.state !== "notStarted").length,
    approved: states.approved ?? 0,
    cardholders: new Set(issued.map((c) => c.owner)).size,
    digitalWallets: new Set(issued.filter((c) => c.tokenWallets?.length).map((c) => c.owner)).size,
    states,
    regionRejected,
  };
}

export interface Bucket {
  start: number;
  end: number;
  count: number;
  micro: bigint | null;
}
export function timeBuckets(
  window: Window,
  events: readonly { date: string; amount?: number }[],
): Bucket[] {
  // Anchored to UTC intervals; display uses the selected timezone, including offset at DST.
  const first = Math.floor(window.start / window.bucketMs) * window.bucketMs;
  const buckets = Array.from(
    { length: Math.floor((window.end - first) / window.bucketMs) + 1 },
    (_, i) => ({
      start: Math.max(window.start, first + i * window.bucketMs),
      end: Math.min(window.end, first + (i + 1) * window.bucketMs),
      count: 0,
      micro: 0n as bigint | null,
    }),
  );
  for (const event of events) {
    if (!inWindow(event.date, window)) continue;
    const bucket = buckets[Math.floor((Date.parse(event.date) - first) / window.bucketMs)];
    bucket.count++;
    const amount = sumMoney([event.amount ?? 0]);
    bucket.micro = bucket.micro === null || amount === null ? null : bucket.micro + amount;
  }
  return buckets;
}

export function receipts(entries: readonly Entry[], window: Window) {
  const rows = unique(entries).filter(
    (e) => e.category === "receivedOnchain" && inWindow(e.occurredAt, window),
  );
  // A wrong-signed receipt is corrupt data, not an amount to silently net against receipts.
  const total = rows.some((e) => e.amountMicroUsdc <= 0)
    ? null
    : sumMoney(rows.map((e) => e.amountMicroUsdc));
  return { rows, total, users: new Set(rows.map((e) => e.owner)).size };
}

export function stateCounts(rows: readonly { state: string }[]): [string, number][] {
  const counts = new Map<string, number>();
  rows.forEach((row) => counts.set(row.state, (counts.get(row.state) ?? 0) + 1));
  return [...counts].sort((a, b) => b[1] - a[1]);
}

export function timeLabel(ms: number, zone: string): string {
  return dayjs(ms).tz(zone).format("DD MMM HH:mm Z");
}
